import { zipSync, strToU8 } from "fflate";

/**
 * Export comptable : CSV français (séparateur « ; », virgule décimale, BOM pour Excel)
 * assemblés dans un ZIP, produit entièrement dans le navigateur.
 */
export type Row = Record<string, unknown>;
export interface ExportData {
  from: string; to: string;
  invoices: Row[]; credit_notes: Row[]; payments: Row[]; leads: Row[]; bank: Row[];
  summary: { invoiced: Record<string, number>; credited: Record<string, number>; collected: Record<string, number>; leads_sold: number };
  company: { legal_name?: string; ein?: string | null; address_line1?: string; city?: string; country?: string } | null;
}

const dateFr = (v: string) => { const d = new Date(v); return isNaN(d.getTime()) ? v : d.toLocaleDateString("fr-FR"); };
const dateTimeFr = (v: string) => { const d = new Date(v); return isNaN(d.getTime()) ? v : d.toLocaleString("fr-FR"); };

/** Une cellule : nombres à la virgule, dates lisibles, booléens en oui/non, guillemets échappés. */
export function cell(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "boolean") return v ? "oui" : "non";
  if (typeof v === "number") return String(v).replace(".", ",");
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return dateFr(s);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return dateTimeFr(s);
  if (/^-?\d+\.\d+$/.test(s)) return s.replace(".", ",");
  return /[;"\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Colonnes dans un ordre décidé ici, pas celui du JSON renvoyé par la base :
 * un comptable doit retrouver les mêmes colonnes au même endroit à chaque export.
 * Chaque entrée : [clé technique, en-tête lisible].
 */
export type Col = [key: string, label: string, money?: true];
export type Cols = Col[];

export const COLS: Record<string, Cols> = {
  invoices: [
    ["numero", "Numéro"], ["type", "Type"], ["date", "Date d'émission"], ["echeance", "Échéance"],
    ["client", "Client"], ["tva_client", "N° TVA client"], ["pays", "Pays"], ["devise", "Devise"],
    ["ht", "Hors taxe", true], ["taxe_label", "Taxe"], ["taxe_taux", "Taux (%)"], ["taxe", "Montant de taxe", true],
    ["ttc", "Total TTC", true], ["acomptes_deduits", "Acomptes déduits", true], ["avoirs", "Avoirs imputés", true],
    ["paye", "Payé", true], ["restant_du", "Restant dû", true], ["statut", "Statut"], ["commande", "Commande"], ["mention", "Mention fiscale"],
  ],
  credit_notes: [
    ["numero", "Numéro"], ["date", "Date d'émission"], ["client", "Client"], ["devise", "Devise"],
    ["ht", "Hors taxe", true], ["taxe", "Montant de taxe", true], ["ttc", "Total TTC", true],
    ["facture_origine", "Facture d'origine"], ["motif", "Motif"], ["statut", "Statut"],
  ],
  payments: [
    ["date", "Date"], ["client", "Client"], ["source", "Source"], ["methode", "Méthode"],
    ["devise", "Devise"], ["montant", "Montant", true], ["factures", "Factures réglées"],
    ["devise_reglement", "Devise de règlement"], ["regle_usd", "Montant réglé", true], ["taux", "Taux de change"],
    ["frais_usd", "Frais", true], ["net_usd", "Net encaissé", true], ["reference", "Référence"], ["test", "Mode test"],
  ],
  leads: [
    ["date_vente", "Date de vente"], ["client", "Client"], ["produit", "Produit"], ["zone", "Zone"],
    ["prix", "Prix unitaire", true], ["devise", "Devise"], ["commande", "Commande"],
    ["remplacement", "Remplacement"], ["statut_lead", "Statut du lead"],
  ],
  bank: [
    ["date", "Date"], ["compte", "Compte"], ["montant", "Montant", true], ["devise", "Devise"],
    ["contrepartie", "Contrepartie"], ["libelle", "Libellé bancaire"], ["statut", "Statut"],
    ["rapprochement", "Rapprochement"], ["rapproche_avec", "Rapproché avec"],
  ],
  summary: [["poste", "Poste"], ["montants", "Montants"]],
};

/** Un montant : toujours deux décimales, virgule française (la base renvoie 1200, le comptable attend 1200,00). */
export function amount(v: unknown): string {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  return Number.isNaN(n) ? cell(v) : n.toFixed(2).replace(".", ",");
}

export function toCsv(rows: Row[], cols: Cols): string {
  // Une colonne inattendue (ajoutée côté base) est conservée en fin de ligne : rien ne se perd.
  const known = new Set(cols.map(([k]) => k));
  const extra = rows.length ? Object.keys(rows[0]).filter((k) => !known.has(k)) : [];
  const all: Cols = [...cols, ...extra.map((k) => [k, k] as Col)];
  const lines = [all.map(([, label]) => label).join(";"), ...rows.map((r) => all.map(([k, , isMoney]) => (isMoney ? amount(r[k]) : cell(r[k]))).join(";"))];
  return "﻿" + lines.join("\r\n") + "\r\n";
}

const money = (m: Record<string, number> | undefined) => Object.entries(m ?? {}).map(([c, v]) => `${amount(v)} ${c}`).join(" + ") || "0";

export function buildExportZip(d: ExportData): { blob: Blob; filename: string; rows: number } {
  const { bytes, filename, rows } = buildExportBytes(d);
  // Uint8Array → Blob : copie explicite pour un ArrayBuffer propre
  return { blob: new Blob([bytes.slice()], { type: "application/zip" }), filename, rows };
}

/** Le ZIP sous forme d'octets — testable sans passer par un Blob. */
export function buildExportBytes(d: ExportData): { bytes: Uint8Array; filename: string; rows: number } {
  const period = `${d.from}_${d.to}`;
  const summaryRows: Row[] = [
    { poste: "Facturé (hors avoirs)", montants: money(d.summary.invoiced) },
    { poste: "Avoirs émis", montants: money(d.summary.credited) },
    { poste: "Encaissé", montants: money(d.summary.collected) },
    { poste: "Leads vendus", montants: String(d.summary.leads_sold) },
    { poste: "Factures", montants: String(d.invoices.length) },
    { poste: "Paiements", montants: String(d.payments.length) },
  ];
  const readme = [
    `EXPORT COMPTABLE — ${d.company?.legal_name ?? "IRONCLIC, Inc."}${d.company?.ein ? ` (EIN ${d.company.ein})` : ""}`,
    `Période : du ${dateFr(d.from)} au ${dateFr(d.to)}`,
    `Généré le : ${new Date().toLocaleString("fr-FR")}`,
    "",
    "Fichiers :",
    "  factures.csv       — factures émises (hors brouillons et hors avoirs) : HT, taxe, TTC, acomptes déduits, payé, restant dû",
    "  avoirs.csv         — avoirs émis, avec la facture d'origine et le motif",
    "  paiements.csv      — paiements encaissés : montant dans la devise de la facture, montant réglé sur le compte, taux, frais",
    "  leads-vendus.csv   — ventes de leads (date, client, produit, zone, prix, commande)",
    "  banque.csv         — transactions bancaires de la période et leur rapprochement",
    "  recapitulatif.csv  — totaux par devise",
    "",
    "Format : séparateur « ; », décimales à la virgule, encodage UTF-8 avec BOM (Excel français), dates JJ/MM/AAAA.",
    "Les montants ne sont jamais convertis : chaque devise est présentée séparément.",
    "Une facture émise ne change jamais ; toute correction passe par un avoir.",
  ].join("\r\n");

  const files: Record<string, Uint8Array> = {
    "LISEZ-MOI.txt": strToU8(readme),
    "factures.csv": strToU8(toCsv(d.invoices, COLS.invoices)),
    "avoirs.csv": strToU8(toCsv(d.credit_notes, COLS.credit_notes)),
    "paiements.csv": strToU8(toCsv(d.payments, COLS.payments)),
    "leads-vendus.csv": strToU8(toCsv(d.leads, COLS.leads)),
    "banque.csv": strToU8(toCsv(d.bank, COLS.bank)),
    "recapitulatif.csv": strToU8(toCsv(summaryRows, COLS.summary)),
  };
  const rows = d.invoices.length + d.credit_notes.length + d.payments.length + d.leads.length + d.bank.length;
  return { bytes: zipSync(files, { level: 6 }), filename: `export-comptable-${period}.zip`, rows };
}
